import { IComboBox, IComboBoxOption, IComboBoxProps } from '@fluentui/react';
import { IPComboBox, IPStack } from 'baseComponents';
import { ISingleTag, SingleTags } from 'components/Tags/SingleTag';
import { globalComboContainerHeight, globalStackTokensGapSmall } from 'globalStyles';
import { useEffect, useMemo, useRef, useState } from 'react';
import { sortOnString } from '../../utils/sorting';

interface IComboWithFilterProps extends Omit<IComboBoxProps, 'selectedKey' | 'defaultSelectedKey'> {
  tagColor?: string;
  noMatchText?: string;
  selectedKeys: string[];
  onSelectedKeysChange: (selectedKeys: string[]) => void;
}

export const ComboWithFilter = (props: IComboWithFilterProps) => {
  const [query, setQuery] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const comboBoxRef = useRef<IComboBox | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const ignoreNextFocusOpenRef = useRef(false);

  const {
    options,
    disabled,
    onFocus: onFocusProp,
    onMenuOpen: onMenuOpenProp,
    onMenuDismissed: onMenuDismissedProp,
    onInputValueChange: onInputValueChangeProp,
    onChange: onChangeProp,
    placeholder,
    tagColor,
    noMatchText,
    selectedKeys,
    onSelectedKeysChange,
    ...rest
  } = props;

  const allOptions = useMemo(() => options ?? [], [options]);

  const normalizedSelectedKeys = useMemo(() => {
    return selectedKeys.map((key) => String(key));
  }, [selectedKeys]);

  const popupSelector = '.ms-ComboBox-callout, .ms-Callout, .ms-ComboBox-optionsContainerWrapper';
  const caretButtonSelector = '.ms-ComboBox-CaretDown-button, .ms-ComboBox button, button[aria-expanded]';

  const updateSelectedKeys = (updater: (prev: string[]) => string[]) => {
    onSelectedKeysChange(updater(normalizedSelectedKeys));
  };

  useEffect(() => {
    if (disabled) {
      comboBoxRef.current?.dismissMenu();
      setIsMenuOpen(false);
      setQuery('');
      ignoreNextFocusOpenRef.current = false;
    }
  }, [disabled]);

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    const isInsideCombo = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;

      if (!el) {
        return false;
      }

      if (rootRef.current?.contains(el)) {
        return true;
      }

      return !!el.closest(popupSelector);
    };

    const handleMouseDown = (ev: MouseEvent) => {
      if (!isInsideCombo(ev.target)) {
        comboBoxRef.current?.dismissMenu();
      }
    };

    const handleFocusIn = (ev: FocusEvent) => {
      if (!isInsideCombo(ev.target)) {
        comboBoxRef.current?.dismissMenu();
      }
    };

    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('focusin', handleFocusIn, true);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('focusin', handleFocusIn, true);
    };
  }, [isMenuOpen]);

  const filteredOptions = useMemo((): IComboBoxOption[] => {
    let availableOptions = [...allOptions];

    if (query) {
      const q = query.toLowerCase();

      availableOptions = availableOptions.filter(
        (o) => o.text.toLowerCase().includes(q) || o.title?.toLowerCase().includes(q),
      );
    }

    if (availableOptions.length === 0 && noMatchText) {
      return [
        {
          key: 'no-match',
          text: noMatchText,
          disabled: true,
        },
      ];
    }

    return availableOptions.sort((a, b) => sortOnString(a.text, b.text));
  }, [allOptions, noMatchText, query]);

  const tags = useMemo((): ISingleTag[] => {
    return allOptions
      .filter((option) => normalizedSelectedKeys.includes(String(option.key)))
      .map((option) => ({
        tagId: String(option.key),
        tagName: option.text || '',
        tagColor: tagColor || '#c3d1eb',
      }))
      .sort((a, b) => sortOnString(a.tagName, b.tagName));
  }, [allOptions, normalizedSelectedKeys, tagColor]);

  return (
    <IPStack tokens={globalStackTokensGapSmall}>
      <IPStack.Item>
        <div
          ref={rootRef}
          onMouseDownCapture={(ev) => {
            const target = ev.target as HTMLElement | null;
            ignoreNextFocusOpenRef.current = !!target?.closest(caretButtonSelector);
          }}
        >
          <IPComboBox
            {...rest}
            disabled={disabled}
            componentRef={comboBoxRef}
            placeholder={placeholder}
            options={filteredOptions}
            multiSelect
            selectedKey={normalizedSelectedKeys}
            allowFreeform
            useComboBoxAsMenuWidth
            styles={globalComboContainerHeight}
            text={isMenuOpen ? query : ''}
            onChange={(ev, option, index, value) => {
              onChangeProp?.(ev, option, index, value);

              const id = option?.key;

              if (id === undefined || id === null) {
                return;
              }

              const normalizedId = String(id);

              if (normalizedId === 'no-match') {
                return;
              }

              updateSelectedKeys((prev) => {
                if (prev.includes(normalizedId)) {
                  return prev.filter((key) => key !== normalizedId);
                }

                return [...prev, normalizedId];
              });
            }}
            onInputValueChange={(value) => {
              const nextValue = value ?? '';
              setQuery(nextValue);
              onInputValueChangeProp?.(value);
            }}
            onFocus={(ev) => {
              onFocusProp?.(ev);

              if (disabled) {
                return;
              }

              if (ignoreNextFocusOpenRef.current) {
                ignoreNextFocusOpenRef.current = false;

                return;
              }

              if (!isMenuOpen) {
                comboBoxRef.current?.focus(true);
              }
            }}
            onMenuOpen={() => {
              setIsMenuOpen(true);
              onMenuOpenProp?.();
            }}
            onMenuDismissed={() => {
              setIsMenuOpen(false);
              setQuery('');
              ignoreNextFocusOpenRef.current = false;
              onMenuDismissedProp?.();
            }}
          />
        </div>
      </IPStack.Item>
      {tags.length > 0 && (
        <IPStack.Item>
          <SingleTags
            tags={tags}
            onRemove={(item) => {
              updateSelectedKeys((prev) => prev.filter((key) => key !== item.tagId));
            }}
            maxWidth={300}
          />
        </IPStack.Item>
      )}
    </IPStack>
  );
};
